import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const queryErr = new URLSearchParams(window.location.search).get('error');
    if (queryErr) {
      setError(decodeURIComponent(queryErr.replace(/\+/g, ' ')));
      return;
    }

    const raw = window.location.hash?.replace(/^#/, '') ?? '';
    const params = new URLSearchParams(raw);
    const token = params.get('access_token');
    const type = params.get('type');
    const err = params.get('error_description') || params.get('error');

    if (err) {
      setError(decodeURIComponent(err.replace(/\+/g, ' ')));
      return;
    }

    if (!token || type !== 'recovery') {
      setError('This reset link is invalid or has expired. Please request a new one.');
      return;
    }

    setAccessToken(token);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!accessToken) {
      setError('Missing recovery token.');
      return;
    }
    setBusy(true);
    try {
      await authService.completePasswordReset(accessToken, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not update password. Try requesting a new link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Set new password - Arrangelly</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-gradient-to-br from-slate-800/80 to-purple-900/20 border border-white/10 rounded-2xl p-8 backdrop-blur-md">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Set a new password</h1>
          {done ? (
            <div className="p-3 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-lg text-sm">
              Password updated. Redirecting to sign in…
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {accessToken && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="np">
                      New password
                    </label>
                    <input
                      id="np"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="w-full rounded-lg bg-slate-900/60 border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="npc">
                      Confirm password
                    </label>
                    <input
                      id="npc"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="w-full rounded-lg bg-slate-900/60 border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Repeat password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="w-full mt-2 bg-accent hover:bg-accent/90 text-white disabled:opacity-50"
                  >
                    {busy ? 'Saving…' : 'Update password'}
                  </Button>
                </form>
              )}
              <p className="mt-6 text-xs text-gray-400 text-center">
                <Link to="/login" className="text-purple-300 hover:text-purple-200 underline">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
